import * as THREE from '@phdsoft/three';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { PointShapeType } from '../digital-twin/graphics/point-shape-geometry-cache';

/**
 * Pure helpers for "change inspection target" (retarget an inspection to another
 * structural element, possibly in another section of the same region).
 * No Angular, no scene access: the controller resolves the elements and passes them in.
 */

export type RetargetMethod = 'POINT' | 'AREA' | 'ELEMENT' | 'OTHER';

export type RetargetPointType = 'Plate' | 'Stiffener' | 'Bracket' | 'FacePlate' | 'BeamPart' | 'Cable' | 'Pipe';

/** Element-type id as stored on elements (typed as the ELEMENTSTYPESIDS class on WorkableElement, a GUID string at runtime). */
export type ElementTypeIdLike = string | ELEMENTSTYPESIDS;

/** The fields of a WorkableElement the helpers read (a WorkableElement satisfies it). */
export interface RetargetTargetLike {
  Guid: string;
  TypeId: ElementTypeIdLike;
  SectionId?: string;
  regionId?: string;
  regionIds?: Array<string>;
  properties?: any;
  Campaign?: { Type?: string } | null;
}

/** The fields of a GaugingElement the helpers read (a GaugingElement satisfies it). */
export interface RetargetInspectionLike {
  regionIds?: Array<string>;
  targetElement?: { Guid: string } | null;
  elementResult?: Partial<ElementResultInfo> | null;
  checkList?: Array<RetargetChecklistItemLike | null> | null;
}

export type RetargetValidation = { ok: true } | { ok: false; reason: string };

/** Maps an `InspectionMethodId` (INSERTIONCONSTANTS) to the retarget flavour. */
export function retargetMethodOf(inspectionMethodId: string | null | undefined): RetargetMethod {
  switch (inspectionMethodId) {
    case INSERTIONCONSTANTS.POINT:
      return 'POINT';
    case INSERTIONCONSTANTS.AREA:
      return 'AREA';
    case INSERTIONCONSTANTS.ELEMENT:
      return 'ELEMENT';
    default:
      return 'OTHER';
  }
}

/**
 * Inspection scope for a target element. Single implementation shared with
 * drag-to-move (`AssetManagerInspectionDrag`).
 *
 * - Plate (and 'Solid') -> 'ONLY PLATE', FacePlate -> 'ONLY FACE PLATE',
 *   BeamPart -> 'ONLY BEAM PART', Cable -> 'ONLY CABLE', Pipe -> 'ONLY PIPE'.
 * - Stiffener/Bracket -> 'ONLY WEB' when `properties.Geometry.WebThickness` is set,
 *   else 'ONLY FLANGE' when `FlangeThickness` is set, else `null`: the scope cannot be
 *   resolved and the caller must reject the target.
 * - Any other type -> `currentScope` unchanged (`undefined` when not given); the backend
 *   has no scope rule for those types.
 *
 * So `null` always means "reject" and never "keep".
 */
export function scopeForTarget(target: { TypeId: ElementTypeIdLike; properties?: any }, currentScope?: string): string | null | undefined {
  switch (ELEMENTSTYPESIDS.getElementTypeName(target.TypeId as string)) {
    case 'Plate':
    case 'Solid':
      return 'ONLY PLATE';
    case 'FacePlate':
      return 'ONLY FACE PLATE';
    case 'BeamPart':
      return 'ONLY BEAM PART';
    case 'Cable':
      return 'ONLY CABLE';
    case 'Pipe':
      return 'ONLY PIPE';
    case 'Stiffener':
    case 'Bracket':
      if (target.properties?.Geometry?.WebThickness) {
        return 'ONLY WEB';
      } else if (target.properties?.Geometry?.FlangeThickness) {
        return 'ONLY FLANGE';
      }
      return null;
    default:
      return currentScope;
  }
}

/**
 * Instanced batch shape of a POINT inspection with this scope: dev's `_scopeToShapeType`
 * (asset-manager-inspection / asset-manager-downloaded-inspection). Unknown or missing -> Plate.
 */
export function pointShapeForScope(scope: string | null | undefined): PointShapeType {
  switch (scope) {
    case 'ONLY PLATE': return PointShapeType.Plate;
    case 'ONLY WEB': return PointShapeType.Web;
    case 'ONLY FLANGE': return PointShapeType.Flange;
    case 'ONLY FACE PLATE': return PointShapeType.FacePlate;
    case 'ONLY BEAM PART': return PointShapeType.BeamPart;
    case 'ONLY CABLE': return PointShapeType.Cable;
    case 'ONLY PIPE': return PointShapeType.Pipe;
    default: return PointShapeType.Plate;
  }
}

/**
 * Point type expected by the decal/point builders for a target element, or `null`
 * when the element type cannot carry an inspection point. 'Solid' maps to 'Plate'.
 */
export function pointTypeForTarget(target: { TypeId: ElementTypeIdLike }): RetargetPointType | null {
  const name = ELEMENTSTYPESIDS.getElementTypeName(target.TypeId as string);
  switch (name) {
    case 'Plate':
    case 'Solid':
      return 'Plate';
    case 'Stiffener':
    case 'Bracket':
    case 'FacePlate':
    case 'BeamPart':
    case 'Cable':
    case 'Pipe':
      return name as RetargetPointType;
    default:
      return null;
  }
}

/**
 * World-space unit normal of the hit face (`face.normal` is in object space).
 * Returns `null` when the intersection has no face (points/lines).
 */
export function worldNormalFromIntersection(i: THREE.Intersection): THREE.Vector3 | null {
  if (!i?.face?.normal || !i.object) return null;
  return i.face.normal.clone().transformDirection(i.object.matrixWorld).normalize();
}

/** Options of `validateRetarget`: regions chosen in the dialog that are allowed besides the inspection's own. */
export interface RetargetValidateOptions {
  extraRegionIds?: Array<string>;
}

/** Checklist fields `crossRegionBlockReason` reads (a ChecklistItem satisfies it). */
export interface RetargetChecklistItemLike {
  Date?: Date | string | null;
  ActionPlanId?: string | null;
  CriticalAreaId?: string | null;
}

/** The fields of a GaugingElement `crossRegionBlockReason` reads (a GaugingElement satisfies it). */
export interface RetargetCrossRegionInspectionLike {
  elementResult?: { InspectionMethodId?: string; CheckList?: any } | null;
  checkList?: Array<RetargetChecklistItemLike | null> | null;
}

/** Region fields `retargetRegionOptions` reads; the caller derives `hasElements` from the loaded region. */
export interface RetargetRegionLike {
  Guid: string;
  Name: string;
  hasElements: boolean;
}

export interface RetargetRegionOption {
  id: string;
  label: string;
  isInspectionRegion: boolean;
  isShown: boolean;
}

/**
 * Why `inspection` cannot move to another region, or `null` when it can.
 * Only POINT inspections can move (AREA/ELEMENT/OTHER cannot); a POINT inspection whose
 * checklist (`checkList`, else `elementResult.CheckList`) has an item with an
 * `ActionPlanId` or a `CriticalAreaId` cannot move either.
 */
export function crossRegionBlockReason(inspection: RetargetCrossRegionInspectionLike): string | null {
  if (retargetMethodOf(inspection?.elementResult?.InspectionMethodId) !== 'POINT') {
    return 'Only inspection points can move to another region.';
  }
  const checkList: Array<RetargetChecklistItemLike | null> = [
    ...(Array.isArray(inspection.checkList) ? inspection.checkList : []),
    ...(Array.isArray(inspection.elementResult?.CheckList) ? inspection.elementResult.CheckList : []),
  ];
  if (checkList.some(item => !!item?.ActionPlanId || !!item?.CriticalAreaId)) {
    return 'Inspections linked to an action plan or critical area cannot move to another region.';
  }
  return null;
}

/**
 * Whether `target` is an acceptable new target for `inspection`.
 * Rejects: no target; a target outside a StructureCampaign; the current target (except for POINT
 * inspections, where it means a new position on the same element); a target outside the allowed
 * regions; a Stiffener/Bracket whose scope cannot be resolved.
 * Cross-section within a region is allowed; AREA inspections are allowed within their region.
 *
 * Without `opts` only the inspection's regions are allowed (phase-1 behaviour and reason).
 * With `opts` the allowed regions are the inspection's regions plus `opts.extraRegionIds`;
 * a target in another region is a cross-region move and is rejected with
 * `crossRegionBlockReason(inspection)` when that is non-null, else with
 * "Choose the target's region in the dialog first." when its region is not in the extras.
 */
export function validateRetarget(inspection: RetargetInspectionLike, target: RetargetTargetLike, opts?: RetargetValidateOptions): RetargetValidation {
  if (!inspection) {
    return { ok: false, reason: 'No inspection selected.' };
  }
  if (!target) {
    return { ok: false, reason: 'No target element selected.' };
  }
  if (target.Campaign?.Type !== 'StructureCampaign') {
    return { ok: false, reason: 'Inspections can only target structural elements.' };
  }

  const currentTargetId = inspection.targetElement?.Guid ?? inspection.elementResult?.TargetGeometryId;
  // a POINT on its current target is a reposition; for other methods nothing would change
  if (currentTargetId && target.Guid === currentTargetId
    && retargetMethodOf(inspection.elementResult?.InspectionMethodId) !== 'POINT') {
    return { ok: false, reason: 'The inspection already targets this element.' };
  }

  const inspectionRegionIds = inspection.regionIds?.length
    ? inspection.regionIds
    : (inspection.elementResult?.RegionIds ?? []);
  const crossRegion = !target.regionId || !inspectionRegionIds.includes(target.regionId);
  if (crossRegion) {
    if (!opts) {
      return { ok: false, reason: 'The new target must be in the same region as the inspection.' };
    }
    const blockReason = crossRegionBlockReason(inspection);
    if (blockReason) {
      return { ok: false, reason: blockReason };
    }
    if (!target.regionId || !(opts.extraRegionIds ?? []).includes(target.regionId)) {
      return { ok: false, reason: "Choose the target's region in the dialog first." };
    }
  }

  if (scopeForTarget(target) === null) {
    return { ok: false, reason: 'The inspection scope (web/flange) of the selected element cannot be determined.' };
  }

  return { ok: true };
}

/**
 * New ElementResultInfo for `current` retargeted to `target`. Never mutates `current`;
 * the `Element` back-reference is omitted (the GaugingElement.elementResult setter re-links it).
 *
 * Sets SectionId, TargetGeometryId, TargetGeometryIds ([target.Guid]), TargetGeometryTypeId
 * (target.TypeId, the element-type GUID, same as insertion), RegionIds, InsertedRegionId and
 * Scope (`scopeForTarget(target, current.Scope)`, i.e. current scope kept for types without a rule).
 * For POINT inspections, `ElementInfo.vertices`/`normal` are replaced from `opts.point`/`opts.normal`
 * when given; ElementInfo is left as is for every other method.
 *
 * Throws when the scope cannot be resolved: call `validateRetarget` first.
 */
export function buildRetargetedResult(
  current: ElementResultInfo,
  target: RetargetTargetLike,
  opts: { point?: THREE.Vector3; normal?: THREE.Vector3 },
): ElementResultInfo {
  const scope = scopeForTarget(target, current.Scope);
  if (scope === null) {
    throw new Error(`buildRetargetedResult: cannot resolve the scope of target ${target.Guid}`);
  }

  const { Element: _omitted, ...rest } = current;

  const result = {
    ...rest,
    SectionId: target.SectionId,
    TargetGeometryId: target.Guid,
    TargetGeometryIds: [target.Guid],
    TargetGeometryTypeId: target.TypeId as ELEMENTSTYPESIDS,
    RegionIds: [...(target.regionIds ?? [])],
    InsertedRegionId: target.regionId,
    Scope: scope,
  } as ElementResultInfo;

  if (retargetMethodOf(current.InspectionMethodId) === 'POINT' && (opts?.point || opts?.normal)) {
    const info = current.ElementInfo;
    result.ElementInfo = {
      ...(info ?? { type: 'point', vertices: [] }),
      vertices: opts.point ? [opts.point.x, opts.point.y, opts.point.z] : [...(info?.vertices ?? [])],
      normal: opts.normal ? [opts.normal.x, opts.normal.y, opts.normal.z] : info?.normal ? [...info.normal] : undefined,
    };
  }

  return result;
}

/**
 * Follow-up date for `saveInspectionElement`: the date of the checklist item that has
 * PlanIds (the last one, as the legacy flow did); else the latest checklist day (local
 * midnight), since loaded points carry no PlanIds client-side and the save creates/updates
 * the plan around this date; only when the checklist has no valid date, `fallback` (the
 * caller passes `assetManager.referenceDate`). Never returns null (fixes D3).
 */
export function followupDateFor(checkList: any[], fallback: Date): Date {
  let date: Date = null;
  for (const item of checkList ?? []) {
    if (item?.PlanIds?.length > 0 && item.Date != null) {
      const d = new Date(item.Date);
      if (!isNaN(d.getTime())) {
        date = d;
      }
    }
  }
  if (date) return date;
  const days = distinctChecklistDays(checkList ?? []);
  return days.length ? days[days.length - 1] : fallback;
}

/**
 * Region dropdown options for a cross-region retarget. Regions without elements are dropped.
 * Order: the inspection region(s) (in `regions` order), then the shown region, then the rest
 * by `Name` (natural order). Labels get " (inspection)" and/or " (shown)".
 */
export function retargetRegionOptions(
  regions: Array<RetargetRegionLike>,
  inspectionRegionIds: Array<string>,
  shownRegionId: string | null,
): Array<RetargetRegionOption> {
  const inspectionIds = new Set(inspectionRegionIds ?? []);
  const rank = (r: RetargetRegionLike) => (inspectionIds.has(r.Guid) ? 0 : r.Guid === shownRegionId ? 1 : 2);
  return (regions ?? [])
    .filter(r => !!r?.hasElements)
    .map((r, index) => ({ r, index }))
    .sort((a, b) => {
      const byRank = rank(a.r) - rank(b.r);
      if (byRank !== 0) return byRank;
      if (rank(a.r) === 2) {
        const byName = (a.r.Name ?? '').localeCompare(b.r.Name ?? '', undefined, { numeric: true });
        if (byName !== 0) return byName;
      }
      return a.index - b.index;
    })
    .map(({ r }) => {
      const isInspectionRegion = inspectionIds.has(r.Guid);
      const isShown = shownRegionId != null && r.Guid === shownRegionId;
      const label = `${r.Name ?? r.Guid}${isInspectionRegion ? ' (inspection)' : ''}${isShown ? ' (shown)' : ''}`;
      return { id: r.Guid, label, isInspectionRegion, isShown };
    });
}

/** Distinct checklist days (local midnight), ascending; items without a valid `Date` are skipped. */
export function distinctChecklistDays(checkList: Array<{ Date?: Date | string | null } | null>): Date[] {
  const days = new Set<number>();
  for (const item of checkList ?? []) {
    if (item?.Date == null) continue;
    const d = new Date(item.Date);
    if (isNaN(d.getTime())) continue;
    d.setHours(0, 0, 0, 0);
    days.add(d.getTime());
  }
  return [...days].sort((a, b) => a - b).map(t => new Date(t));
}

export interface ChecklistDay { day: Date; executed: boolean }

/**
 * Checklist items grouped by calendar day (local midnight), ascending; a day is executed when any
 * of its items has a non-empty Value. Items without a valid Date are ignored.
 */
export function checklistDaysWithExecution(checkList: Array<{ Date?: Date | string | null; Value?: any } | null>): ChecklistDay[] {
  const days = new Map<number, boolean>();
  for (const item of checkList ?? []) {
    if (item?.Date == null) continue;
    const d = new Date(item.Date);
    if (isNaN(d.getTime())) continue;
    d.setHours(0, 0, 0, 0);
    const executed = item.Value != null && item.Value !== '';
    days.set(d.getTime(), (days.get(d.getTime()) ?? false) || executed);
  }
  return [...days.entries()].sort(([a], [b]) => a - b).map(([t, executed]) => ({ day: new Date(t), executed }));
}
